import React, { useMemo, useState } from "react"
import { FaBookOpen, FaCode, FaCopy, FaLayerGroup, FaRoute, FaSearch } from "react-icons/fa"
import { Link } from "react-router-dom"

const methodStyle = {
  GET: { background: "#dcfce7", color: "#166534" },
  POST: { background: "#dbeafe", color: "#1d4ed8" },
  PUT: { background: "#ffedd5", color: "#c2410c" },
  PATCH: { background: "#f3e8ff", color: "#7e22ce" },
  DELETE: { background: "#fee2e2", color: "#b91c1c" },
  FETCH: { background: "#e0f2fe", color: "#0369a1" },
}

const apiBase = process.env.REACT_APP_API_BASE_URL || "https://<ats-host>"

const commonResponses = [
  {
    status: "200",
    meaning: "Success",
    body: "{ results: [], count: 0, next: null, previous: null } for paginated lists, or the saved object for detail APIs.",
  },
  {
    status: "201",
    meaning: "Created",
    body: "The created candidate, company, job, event, note, user, role, email template, or TalentScan link object.",
  },
  {
    status: "204",
    meaning: "Deleted",
    body: "Empty response after delete endpoints complete successfully.",
  },
  {
    status: "400",
    meaning: "Bad Request",
    body: "{ detail: string } or field validation errors keyed by request field name.",
  },
  {
    status: "401/403",
    meaning: "Auth/Permission",
    body: "{ detail: 'Authentication credentials were not provided.' } or permission denied message.",
  },
  {
    status: "500",
    meaning: "Server Error",
    body: "{ detail: 'Server error.' } or { error: string } for custom API views.",
  },
]

const pageRoutes = [
  ["Dashboard", "/ats-dashboard", "Manager dashboard KPIs, activity, pipeline, interview summaries"],
  ["Dashboard", "/recruiter-dashboard", "Recruiter-owned counts, activity, placement and follow-up data"],
  ["Candidates", "/allactive-candidates", "All active candidates list, filters, actions and notes"],
  ["Candidates", "/candidates-added", "Candidates created by the logged-in user"],
  ["Candidates", "/candidates-opened", "Candidates opened by the logged-in user"],
  ["Candidates", "/candidates-duplicate", "Duplicate candidate review queue"],
  ["Candidates", "/myactive-candidates", "My active candidates"],
  ["Candidates", "/candidates-activity", "Candidate activity log view"],
  ["Candidates", "/candidates-archived", "Archived candidates"],
  ["Candidates", "/candidates-tasklist", "Candidate tasks and follow-ups"],
  ["Candidates", "/candidate-editform/:id", "Candidate edit form"],
  ["Candidates", "/candidate-postform", "Candidate create form"],
  ["Candidates", "/uploadresumeform", "Single resume upload"],
  ["Candidates", "/add-candidate", "Stepper candidate creation"],
  ["Candidates", "/all_candidates", "ATS all candidates"],
  ["Candidates", "/selected_candidates", "Selected candidates"],
  ["Candidates", "/rejected_candidates", "Rejected candidates"],
  ["Candidates", "/opt-out-candidates", "Opt-out candidates"],
  ["Candidates", "/all_referral_candidates", "Referral candidates"],
  ["Search", "/search-results", "Global advanced search"],
  ["Contacts", "/all-employees", "All active contacts"],
  ["Contacts", "/contacts-tasklist", "Contact tasks"],
  ["Contacts", "/contacts-activity", "Contact activity log"],
  ["Contacts", "/contacts-myactive", "My active contacts"],
  ["Contacts", "/contacts-archived", "Archived contacts"],
  ["Contacts", "/contacts-duplicate", "Duplicate contact list"],
  ["Contacts", "/contacts-editform", "Contact edit form"],
  ["Companies", "/organization-companies", "Organization companies list"],
  ["Companies", "/companies-myactive", "My active companies"],
  ["Companies", "/companies-archived", "Archived companies"],
  ["Companies", "/companies-tasklist", "Company task list"],
  ["Companies", "/companies-activity", "Company activity log"],
  ["Companies", "/companies-duplicatelist", "Duplicate companies"],
  ["Companies", "/companies-editform", "Company edit form"],
  ["Job Orders", "/all-job-orders", "All job orders and candidates linked to jobs"],
  ["Job Orders", "/my-job-orders", "Job orders owned by the logged-in user"],
  ["Job Orders", "/create-job-orders-view", "Create job order"],
  ["Job Orders", "/create-job-orders-view/:id", "Edit job order"],
  ["Job Orders", "/archived-job-orders", "Archived job orders"],
  ["Job Orders", "/joballclosed-jobs", "Closed jobs"],
  ["Job Orders", "/myactive-jobs", "My active jobs"],
  ["Job Orders", "/jobactivitylog-jobs", "Job activity log"],
  ["Job Orders", "/assignedtome-jobs", "Jobs assigned to me"],
  ["Job Orders", "/candidate-details-view", "Job candidate details"],
  ["Job Orders", "/job-time-line", "Job timeline"],
  ["Job Orders", "/aiReport", "AI screening report detail"],
  ["Job Orders", "/job-order-excel-preview", "Recruiter tracking Excel preview"],
  ["Pipeline", "/candidate-pipeline", "Kanban pipeline by stage"],
  ["Pipeline", "/tableview", "Pipeline table view"],
  ["Calendar", "/my-calendar", "My calendar"],
  ["Calendar", "/todays-scheduled", "Today schedule"],
  ["Calendar", "/all-calendar", "All calendar events"],
  ["Calendar", "/archive-calendar", "Archived calendar events"],
  ["Reports", "/activejob-summary", "Active job summary report"],
  ["Reports", "/login-activity-report", "Login activity report"],
  ["Reports", "/recruiter-performance-report", "Recruiter performance report"],
  ["Reports", "/my-placement-report", "My placement report"],
  ["Reports", "/time-to-hire", "Time to hire report"],
  ["Reports", "/source-performance-report", "Source performance report"],
  ["Reports", "/call-report", "Call report"],
  ["Reports", "/pipeline-report", "Pipeline report"],
  ["Reports", "/tracker-report", "Tracker report"],
  ["Reports", "/placement-report", "Placement report"],
  ["Reports", "/telephonic-tests", "Telephonic and TalentScan tests"],
  ["Reports", "/extension-logs", "Browser/phone extension logs"],
  ["Admin", "/admin-dashboard", "Admin dashboard"],
  ["Admin", "/lookup_Cus", "Lookup administration"],
  ["Admin", "/myAccount", "My account"],
  ["Admin", "/usermanagment", "User management"],
  ["Admin", "/settings", "SMTP, templates, signatures and meeting settings"],
  ["Admin", "/organization-department", "Department management"],
  ["Admin", "/organization-designation", "Designation management"],
  ["Agents", "/agentList", "TalentScan agent list"],
  ["Agents", "/agentcreation", "Create or update TalentScan agent"],
  ["TalentScan", "/tsLogs", "TalentScan parsing logs"],
  ["TalentScan", "/tsScreen", "TalentScan candidate screen logs"],
  ["Social", "/socialMedia", "Social media configuration"],
  ["Emails", "/outbox", "Email outbox"],
  ["Emails", "/sentemails", "Sent emails"],
  ["Imports", "/import-bulkresume", "Bulk resume import"],
  ["Imports", "/importfromcsv-candidates", "Candidate CSV import"],
  ["Imports", "/importfromcsv-companies", "Company CSV import"],
  ["Imports", "/importfromcsv-contacts", "Contact CSV import"],
  ["Imports", "/importfromcsv-jobs", "Jobs CSV import"],
  ["Logs", "/logs", "External API log viewer"],
  ["Docs", "/api-documentation", "This API documentation page"],
]

const modules = [
  {
    name: "Authentication, Layout and User Access",
    pages: ["/ats-dashboard", "/recruiter-dashboard", "/usermanagment", "/myAccount", "/settings"],
    tasks: [
      "Login, auto-login, token storage and global Authorization header setup",
      "Load menu data, user permissions and notification state for the shell",
      "Create, edit, delete and list users, roles, groups and access permissions",
    ],
    request: "{ username, password } for login; user profile, role and permission payloads for admin screens.",
    response: "JWT auth data, user_details, permissions arrays, menu tree, notifications and paginated user lists.",
    endpoints: [
      ["POST", "/api/v1/auth/login", "Authenticate a user and return tokens plus profile details"],
      ["GET", "/api/v1/autologin/", "Read request data for public auto-login flow"],
      ["GET", "/api/v1/get-menu-data/", "Load parent and child sidebar menu configuration"],
      ["GET", "/api/v1/users/user_permissions/", "Load the current user's module/action permissions"],
      ["GET", "/api/v1/users/", "Paginated user list filtered by company, role or search"],
      ["POST", "/api/v1/users/", "Create user"],
      ["PUT", "/api/v1/users/{id}/", "Update user or my account profile"],
      ["DELETE", "/api/v1/users/{id}/", "Delete user"],
      ["GET", "/api/v1/roles/", "Paginated role list"],
      ["POST", "/api/v1/roles/", "Create role with module permissions"],
      ["PUT", "/api/v1/roles/{id}/", "Update role permissions"],
      ["DELETE", "/api/v1/roles/{id}/", "Delete role"],
      ["GET", "/api/v1/user-group/", "List user groups"],
      ["POST", "/api/v1/user-group/", "Create user group"],
      ["PUT", "/api/v1/user-group/{id}/", "Update user group"],
      ["DELETE", "/api/v1/user-group/{id}/", "Delete user group"],
      ["GET", "/api/v1/view-all-notifications/", "List notifications"],
      ["POST", "/api/v1/mark-read/", "Mark one notification as read"],
      ["POST", "/api/v1/mark-all-read/", "Mark all notifications as read"],
    ],
  },
  {
    name: "Candidates and Resume Processing",
    pages: ["/allactive-candidates", "/candidate-postform", "/candidate-editform/:id", "/import-bulkresume"],
    tasks: [
      "Create, update, archive, de-duplicate and filter candidates",
      "Upload resumes, parse candidate data and import candidates from CSV",
      "Manage candidate education, certificates, work details, folders, notes and activity history",
    ],
    request: "Candidate profile fields, resume files as FormData, filter query params, note/comment payloads.",
    response: "Candidate object, paginated candidate list, parse task id/status, folder contents, note timeline.",
    endpoints: [
      ["GET", "/api/v1/candidates/", "Candidate list with page, size, status and filter params"],
      ["POST", "/api/v1/candidates/", "Create candidate"],
      ["GET", "/api/v1/candidates/{id}/", "Candidate detail"],
      ["PUT", "/api/v1/candidates/{id}/", "Replace candidate"],
      ["PATCH", "/api/v1/candidates/{id}/", "Partial update candidate"],
      ["DELETE", "/api/v1/candidates/{id}/", "Delete/archive candidate"],
      ["GET", "/api/v1/get-all-candidates/", "Dropdown/paginated candidate lookup by company"],
      ["GET", "/api/v1/candidates/candidates-by-job/", "Candidates linked to a job order"],
      ["POST", "/api/v1/candidates/upload-resumes/", "Bulk upload resumes for candidate parsing/linking"],
      ["POST", "/api/v1/import-candidates/", "CSV candidate import"],
      ["GET", "/api/v1/get-import-fields/", "CSV field mapping list"],
      ["POST", "/api/v1/parse-resume/", "Parse one resume"],
      ["GET", "/api/v1/task-status/{task_id}/", "Check async resume parsing status"],
      ["GET", "/api/v1/candidate/fields/", "Candidate model fields for dynamic filters"],
      ["POST", "/api/v1/search/", "Advanced global search across ATS records"],
      ["POST", "/api/v1/notes/", "Create a note for candidate, contact, company or job context"],
      ["GET", "/api/v1/action-logs/", "Candidate/job action timeline"],
      ["GET", "/api/v1/folders/", "List candidate folders"],
      ["POST", "/api/v1/folders/", "Create candidate folder"],
      ["DELETE", "/api/v1/folders/{folder_id}/", "Delete folder"],
      ["GET", "/api/v1/folders/{folder_id}/candidates/", "List folder candidates"],
      ["POST", "/api/v1/folders/{folder_id}/candidates/", "Add candidates to folder"],
      ["DELETE", "/api/v1/folders/{folder_id}/candidates/", "Remove candidates from folder"],
    ],
  },
  {
    name: "Contacts, Companies and Organization",
    pages: ["/all-employees", "/organization-companies", "/companies-editform", "/organization-department"],
    tasks: [
      "Create, update, archive and de-duplicate companies and contacts",
      "Manage company status, documents, departments, designations and lookup/dropdown data",
      "Import company/contact CSV records and link contacts to jobs or candidates",
    ],
    request: "Company/contact profile payloads, status ids, document upload FormData, lookup query params.",
    response: "Company/contact objects, paginated lists, dropdown results, uploaded document metadata.",
    endpoints: [
      ["GET", "/api/v1/company/", "Company list"],
      ["POST", "/api/v1/company/", "Create company"],
      ["GET", "/api/v1/company/{id}/", "Company detail"],
      ["PUT", "/api/v1/company/{id}/", "Update company"],
      ["PATCH", "/api/v1/company/{id}/change-status/", "Change company status"],
      ["POST", "/api/v1/company/import/", "Import companies from CSV"],
      ["GET", "/api/v1/company-status/", "Company status lookup"],
      ["GET", "/api/v1/get-all-companies/", "Company dropdown"],
      ["GET", "/api/v1/get-all-company-size/", "Company size lookup"],
      ["GET", "/api/v1/get-all-industry-type/", "Industry lookup"],
      ["GET", "/api/v1/get-all-document-type/", "Document type lookup"],
      ["POST", "/api/v1/documents/", "Upload company/contact documents"],
      ["GET", "/api/v1/contacts/", "Contact list"],
      ["POST", "/api/v1/contacts/", "Create contact"],
      ["GET", "/api/v1/contacts/{id}/", "Contact detail"],
      ["PUT", "/api/v1/contacts/{id}/", "Update contact"],
      ["DELETE", "/api/v1/contacts/{id}/", "Delete/archive contact"],
      ["POST", "/api/v1/contacts/import/", "Import contacts from CSV"],
      ["GET", "/api/v1/departmentsv1/", "Department list"],
      ["POST", "/api/v1/departmentsv1/", "Create department"],
      ["GET", "/api/v1/designationsv1/", "Designation list"],
      ["POST", "/api/v1/designationsv1/", "Create designation"],
      ["GET", "/api/v1/get-all-countries/", "Country lookup"],
      ["GET", "/api/v1/get-all-states/", "State lookup by country"],
      ["GET", "/api/v1/get-all-cities/", "City lookup by state"],
      ["GET", "/api/v1/get-all-address-label/", "Address label lookup"],
    ],
  },
  {
    name: "Job Orders, Pipeline and Screening",
    pages: ["/all-job-orders", "/create-job-orders-view", "/candidate-pipeline", "/tableview"],
    tasks: [
      "Create and update job orders, publish LinkedIn/social content and manage job status",
      "Link candidates to jobs, change pipeline stage and submit candidates for approval",
      "Generate screening questions, parse JD files and run TalentScan candidate screening",
    ],
    request: "Job payloads, filters, candidate link ids, screening payloads, stage/status ids and FormData for JD/resume files.",
    response: "Job order objects, candidate-job link objects, screening results, generated questions, stage board data.",
    endpoints: [
      ["GET", "/api/v1/create-job-order/", "List job orders"],
      ["POST", "/api/v1/create-job-order/", "Create job order"],
      ["GET", "/api/v1/create-job-order/get-job/", "Fetch job detail by job_id"],
      ["PATCH", "/api/v1/create-job-order/edit/", "Update job order by job_id"],
      ["DELETE", "/api/v1/create-job-order/delete/", "Delete/archive job order"],
      ["GET", "/api/v1/my-jobs/", "Jobs owned or assigned to current user"],
      ["GET", "/api/v1/get-all-jobs/", "Job dropdown"],
      ["GET", "/api/v1/get-all-Job-status/", "Job status lookup"],
      ["PATCH", "/api/v1/jobs/{job_id}/change-status/", "Change job order status"],
      ["GET", "/api/v1/job-texts/", "Read generated social/job texts"],
      ["POST", "/api/v1/parse-jd/", "Parse job description file/text"],
      ["POST", "/api/v1/generate-screening-questions/", "Generate screening questions"],
      ["PATCH", "/api/v1/update-screening-questions/", "Update selected screening questions"],
      ["POST", "/api/v1/generate-section-questions/generate/", "Generate test questions by section"],
      ["GET", "/api/v1/test-types/", "TalentScan test categories"],
      ["GET", "/api/v1/sections/", "Sections for a selected test category"],
      ["POST", "/api/v1/screen/", "Run candidate-job screening"],
      ["GET", "/api/v1/candidate-screen-history/", "Candidate screening history"],
      ["PATCH", "/api/v1/candidate-job-links/{id}/", "Update candidate-job link fields"],
      ["DELETE", "/api/v1/candidate-job-links/{id}/", "Unlink candidate from job"],
      ["PATCH", "/api/v1/change-stage/{job_candidate_id}/", "Move candidate to another stage"],
      ["PATCH", "/api/v1/send-for-approval/send/", "Send candidate submission for approval"],
      ["PATCH", "/api/v1/change-hrms-status/", "Update HRMS sync/status flag"],
      ["GET", "/api/v1/stages/job_candidates_by_stage/", "Kanban candidates grouped by stage"],
      ["GET", "/api/v1/get-all-stages/", "Stage lookup"],
    ],
  },
  {
    name: "TalentScan, Calls, Consent and Agents",
    pages: ["/agentList", "/agentcreation", "/telephonic-tests", "/tsLogs", "/tsScreen", "/opt-out-candidates"],
    tasks: [
      "Create and manage AI call agents and voice options",
      "Send consent mail/SMS, schedule candidate calls and record opt-outs",
      "View call records, primary/secondary evaluation, scores and TalentScan dashboard links",
    ],
    request: "Agent configuration, candidate/job ids, phone and email data, call scheduling payloads, consent token params.",
    response: "Agent list/detail, call records, evaluation JSON, score rows, consent timeline, dashboard link.",
    endpoints: [
      ["GET", "/api/v1/agents/", "List TalentScan agents"],
      ["POST", "/api/v1/agents/", "Create TalentScan agent"],
      ["GET", "/api/v1/agents/?agent_id={id}", "Agent detail"],
      ["PATCH", "/api/v1/agents/?agent_id={id}", "Update agent"],
      ["DELETE", "/api/v1/agents/?agent_id={id}", "Delete agent"],
      ["GET", "/api/v1/voices/", "List voices for agent/call setup"],
      ["POST", "/api/v1/vitel/click2call/", "Start a Vitel click-to-call"],
      ["GET", "/api/v1/extension-call-records/", "Extension call records"],
      ["GET", "/api/v1/call-records/", "Candidate call records"],
      ["GET", "/api/v1/candidate-scores/", "Candidate score list"],
      ["GET", "/api/v1/get-primary-evaluation/", "Primary evaluation detail"],
      ["GET", "/api/v1/get-secondary-evaluation/", "Secondary evaluation detail"],
      ["POST", "/api/v1/send-consent-mail/", "Send candidate consent email"],
      ["GET", "/api/v1/candidate-consent-timeline/{job_candidate_id}/", "Consent timeline"],
      ["POST", "/api/v1/send-bulk-notifications/", "Send bulk candidate notification"],
      ["GET", "/api/v1/opt-out-candidates/", "Opt-out candidates list"],
      ["GET", "/api/v1/check-consent-token/", "Validate consent/unsubscribe token"],
      ["POST", "/api/v1/candidate-talent-scan-result/", "Fetch/create TalentScan result data"],
      ["POST", "/api/v1/ts-view-test/create-dashboard-link/", "Create public TalentScan dashboard link"],
      ["GET", "/api/v1/talent-scan-parsing-logs/", "TalentScan resume parsing logs"],
      ["GET", "/api/v1/ts-candidate-screen/", "TalentScan candidate screen log"],
    ],
  },
  {
    name: "Calendar, Email and Communication",
    pages: ["/my-calendar", "/todays-scheduled", "/outbox", "/sentemails", "/settings"],
    tasks: [
      "Schedule, edit, archive and list interviews/meetings",
      "Manage SMTP accounts, video meeting credentials, email templates and signatures",
      "Send meeting notifications, outbound emails and candidate communication history",
    ],
    request: "Event date/time payloads, attendees, email body/template/signature data and SMTP config.",
    response: "Event objects, meeting links, email queue rows, template/signature objects and send status.",
    endpoints: [
      ["GET", "/api/v1/events/", "Calendar event list"],
      ["POST", "/api/v1/events/", "Create calendar event/interview"],
      ["PATCH", "/api/v1/events/{id}/", "Update calendar event"],
      ["DELETE", "/api/v1/events/{id}/", "Delete/archive event"],
      ["GET", "/api/v1/upcoming-calendar-events/", "Upcoming events"],
      ["POST", "/api/v1/send-meeting-notification/", "Send meeting notification"],
      ["GET", "/api/v1/meeting-data/", "Meeting metadata"],
      ["GET", "/api/v1/emails/", "Email list/outbox"],
      ["POST", "/api/v1/emails/", "Create/send email"],
      ["GET", "/api/v1/smtp-accounts/", "User SMTP accounts"],
      ["POST", "/api/v1/smtp-accounts/", "Create SMTP account"],
      ["PATCH", "/api/v1/smtp-accounts/{id}/", "Update SMTP account"],
      ["DELETE", "/api/v1/smtp-accounts/{id}/", "Delete SMTP account"],
      ["GET", "/api/v1/company-smtp/", "Company SMTP accounts"],
      ["POST", "/api/v1/company-smtp/test/", "Send SMTP test email"],
      ["GET", "/api/v1/video-meeting-credentials/", "Video meeting credentials"],
      ["POST", "/api/v1/video-meeting-credentials/", "Create meeting credentials"],
      ["GET", "/api/v1/email-signatures/", "Email signatures"],
      ["POST", "/api/v1/email-signatures/", "Create email signature"],
      ["GET", "/api/v1/email-templates/", "Email templates"],
      ["POST", "/api/v1/email-templates/", "Create email template"],
      ["GET", "/api/v1/candidate-communication-history/{job_candidate_id}/", "Candidate communication history"],
    ],
  },
  {
    name: "Reports, Logs and External Integrations",
    pages: ["/activejob-summary", "/tracker-report", "/time-to-hire", "/logs", "/socialMedia"],
    tasks: [
      "Render recruiter, placement, source, pipeline, tracker and login activity reports",
      "Inspect external API logs and call/report records",
      "Create LinkedIn/social media previews and publish/delete posts",
    ],
    request: "Report query params such as company_id, recruiter_id, date range, page and page_size.",
    response: "Aggregated stats, report row arrays, download files, API log entries and social post metadata.",
    endpoints: [
      ["GET", "/api/v1/dashboard/stats/", "Dashboard summary stats"],
      ["GET", "/api/v1/dashboard/manager/stats/", "Manager dashboard stats"],
      ["GET", "/api/v1/dashboard/manager/interviews/", "Manager interview data"],
      ["GET", "/api/v1/dashboard/manager/recent-candidates/", "Recent candidates"],
      ["GET", "/api/v1/dashboard/manager/job-handlers/", "Job handler data"],
      ["GET", "/api/v1/dashboard/manager/job-pipeline/", "Job pipeline data"],
      ["GET", "/api/v1/report/", "Recruiter performance report base"],
      ["GET", "/api/v1/report/time_to_hire_report/", "Time to hire report"],
      ["GET", "/api/v1/tracker-report/", "Tracker report"],
      ["GET", "/api/v1/screening-report/", "Screening report"],
      ["GET", "/api/v1/company/{company_id}/recruiter-activity-report/", "Recruiter activity report"],
      ["GET", "/api/v1/company/{company_id}/recruiter-job-stats/", "Recruiter job stats"],
      ["GET", "/api/v1/company/{company_id}/recruiter-performance/download/", "Download recruiter performance"],
      ["GET", "/api/v1/login-activity/", "Login activity report"],
      ["GET", "/api/v1/api-log/", "External API log list"],
      ["POST", "/api/v1/content-preview/", "Generate social content preview"],
      ["POST", "/api/v1/post-to-linkedin/", "Post job content to LinkedIn"],
      ["GET", "/api/v1/linkedin-details/", "LinkedIn account/company details"],
      ["DELETE", "/api/v1/delete-linkedin-post/", "Delete LinkedIn post for job"],
      ["GET", "/api/v1/job-vacancies/", "HRMS job vacancies"],
      ["GET", "/api/v1/my-referrals/", "Referral dashboard list"],
      ["POST", "/api/v1/candidate-reference/", "Create employee referral"],
      ["POST", "/api/v1/referral-action/", "Update referral action"],
    ],
  },
]

const methodBadge = method => {
  const styles = methodStyle[method] || methodStyle.GET
  return (
    <span className="api-docs-method" style={styles}>
      {method}
    </span>
  )
}

const fallbackCopyText = value => {
  if (typeof document === "undefined") return false

  const textarea = document.createElement("textarea")
  textarea.value = value
  textarea.setAttribute("readonly", "")
  textarea.style.position = "fixed"
  textarea.style.left = "-9999px"
  document.body.appendChild(textarea)
  textarea.select()

  let copied = false
  try {
    copied = document.execCommand("copy")
  } catch (error) {
    copied = false
  }

  document.body.removeChild(textarea)
  return copied
}

const copyText = value => {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(value).catch(() => fallbackCopyText(value))
    return true
  }

  return fallbackCopyText(value)
}

const stringifyJson = value => JSON.stringify(value, null, 2)

const normalizePath = path => path.replace(/\{([^}]+)\}/g, "<$1>")

const getSuccessStatus = method => {
  if (method === "POST") return "201"
  if (method === "DELETE") return "204"
  return "200"
}

const getRequestBody = endpoint => {
  const path = endpoint.path.toLowerCase()
  const description = endpoint.description.toLowerCase()

  if (endpoint.method === "GET" || endpoint.method === "DELETE") return null
  if (path.includes("auth/login")) return { username: "user@example.com", password: "********" }
  if (path.includes("users")) {
    return {
      first_name: "Anwar",
      last_name: "Baig",
      email: "user@example.com",
      phone_number: "9999999999",
      company: 1,
      role: 2,
      is_active: true,
    }
  }
  if (path.includes("roles")) {
    return {
      role_name: "Recruiter",
      company: 1,
      parent_id: "1,2,3",
      child_id: "10,11,12",
      add: "10,11",
      edit: "10",
      delete: "",
      view: "10,11,12",
    }
  }
  if (path.includes("candidate") || path.includes("parse-resume") || path.includes("search")) {
    return {
      first_name: "John",
      last_name: "Doe",
      email: "john.doe@example.com",
      mobile_phone: "9999999999",
      current_company: "Example Technologies",
      skills: ["React", "Django"],
      company_id: 1,
    }
  }
  if (path.includes("company")) {
    return {
      company_name: "Example Technologies",
      website: "https://example.com",
      status: 1,
      industry_type: 2,
      company_size: 3,
    }
  }
  if (path.includes("contacts")) {
    return {
      first_name: "Jane",
      last_name: "Smith",
      email: "jane.smith@example.com",
      phone_number: "9999999999",
      company: 1,
    }
  }
  if (path.includes("create-job-order") || path.includes("jobs") || path.includes("screen")) {
    return {
      job_title: "Frontend Developer",
      company_id: 1,
      job_description: "React developer with ATS domain experience",
      openings: 2,
      status: "Active",
    }
  }
  if (path.includes("events") || path.includes("meeting")) {
    return {
      title: "Technical Interview",
      start: "2026-08-04T10:00:00Z",
      end: "2026-08-04T10:30:00Z",
      candidate_id: 101,
      job_id: 55,
      attendees: ["candidate@example.com"],
    }
  }
  if (path.includes("agents")) {
    return {
      agent_name: "Screening Agent",
      voice_id: "voice_001",
      company_id: 1,
      instructions: "Screen candidates for job fit and communication.",
    }
  }
  if (path.includes("email") || path.includes("smtp")) {
    return {
      subject: "Interview Schedule",
      body: "<p>Please confirm your availability.</p>",
      to: ["candidate@example.com"],
      company_id: 1,
    }
  }
  if (path.includes("linkedin") || path.includes("content-preview")) {
    return {
      job_id: 55,
      company_id: 1,
      content: "We are hiring a Frontend Developer.",
    }
  }
  if (path.includes("referral")) {
    return {
      referral_id: 12,
      action: "Shortlisted",
      comments: "Profile moved to recruiter review.",
    }
  }
  if (description.includes("status")) return { status: "Active", reason: "Updated from ATS UI" }

  return {
    id: 1,
    company_id: 1,
    notes: "Payload fields follow the form fields used by this ATS screen.",
  }
}

const getSampleResult = endpoint => {
  const path = endpoint.path.toLowerCase()

  if (path.includes("get-menu-data")) {
    return {
      menu_id: 1,
      menu_name: "Candidates",
      menu_icons: "fas fa-users",
      page_name: null,
      sub_menus: [
        { menu_id: 11, menu_name: "All Candidates", page_name: "/allactive-candidates" },
      ],
    }
  }
  if (path.includes("user_permissions")) {
    return {
      permissions: [
        {
          parent_id: "1,2,3",
          child_id: "10,11,12",
          add: "10,11",
          edit: "10",
          delete: "",
          view: "10,11,12",
        },
      ],
    }
  }
  if (path.includes("auth/login")) {
    return {
      access: "<jwt_access_token>",
      refresh: "<jwt_refresh_token>",
      user_details: {
        user_id: 1,
        full_name: "Anwar Baig",
        email: "user@example.com",
        companies_data: [{ company_id: 1, company_name: "ATS Company", company_domain: "ats" }],
      },
    }
  }
  if (path.includes("notifications")) {
    return {
      result: [
        {
          notifications: [
            { id: 1, title: "Interview scheduled", is_read: false, created_at: "2026-08-04T10:00:00Z" },
          ],
        },
      ],
    }
  }
  if (path.includes("candidate-scores")) {
    return {
      id: 1,
      candidate_name: "John Doe",
      candidate_email: "john.doe@example.com",
      score: 82,
      status: "Completed",
      job_candidate_id: 45,
    }
  }
  if (path.includes("call-records") || path.includes("extension-call-records")) {
    return {
      id: 1,
      candidate_name: "John Doe",
      recruiter_name: "Recruiter",
      call_status: "completed",
      duration: "00:04:18",
      recording_url: "https://example.com/recording.mp3",
    }
  }
  if (path.includes("agents")) {
    return {
      agent_id: "agent_001",
      agent_name: "Screening Agent",
      voice_id: "voice_001",
      status: "active",
    }
  }
  if (path.includes("voices")) {
    return { id: "voice_001", name: "English Interviewer", language: "en-IN" }
  }
  if (path.includes("folders")) {
    return { id: 1, folder_name: "Shortlisted", candidates_count: 8 }
  }
  if (path.includes("candidate")) {
    return {
      candidate_id: 101,
      first_name: "John",
      last_name: "Doe",
      email: "john.doe@example.com",
      mobile_phone: "9999999999",
      current_company: "Example Technologies",
      status: "Active",
      job_candidate_id: 45,
    }
  }
  if (path.includes("company")) {
    return {
      company_id: 1,
      company_name: "Example Technologies",
      company_domain: "example",
      status: "Active",
      industry_type_name: "Information Technology",
    }
  }
  if (path.includes("contacts")) {
    return {
      contact_id: 21,
      first_name: "Jane",
      last_name: "Smith",
      email: "jane.smith@example.com",
      company_name: "Example Technologies",
    }
  }
  if (path.includes("create-job-order") || path.includes("jobs") || path.includes("stages")) {
    return {
      job_id: 55,
      job_title: "Frontend Developer",
      company_name: "Example Technologies",
      status: "Active",
      candidates_count: 12,
      stages: [{ stage_id: 1, stage_name: "Submitted", candidates: [] }],
    }
  }
  if (path.includes("events") || path.includes("calendar") || path.includes("meeting")) {
    return {
      id: 1,
      title: "Technical Interview",
      start: "2026-08-04T10:00:00Z",
      end: "2026-08-04T10:30:00Z",
      meeting_link: "https://meet.example.com/interview",
    }
  }
  if (path.includes("email")) {
    return {
      id: 1,
      subject: "Interview Schedule",
      status: "sent",
      to: ["candidate@example.com"],
      created_at: "2026-08-04T10:00:00Z",
    }
  }
  if (path.includes("report") || path.includes("dashboard") || path.includes("tracker")) {
    return {
      recruiter_name: "Recruiter",
      jobs_added: 4,
      candidates_added: 25,
      shortlisted_count: 8,
      interviews_count: 6,
    }
  }
  if (path.includes("api-log")) {
    return {
      id: 1,
      api_name: "TalentScan Screening",
      method: "POST",
      url: "/api/v1/screen/",
      status_code: 200,
      request_body: {},
      response_body: {},
    }
  }
  if (path.includes("linkedin") || path.includes("content-preview")) {
    return { job_id: 55, post_url: "https://linkedin.com/posts/example", status: "published" }
  }
  if (path.includes("referral")) {
    return { referral_id: 12, candidate_name: "John Doe", status: "Shortlisted", referral_amount: 5000 }
  }

  return { id: 1, name: "Sample record", status: "Active" }
}

const getSampleResponse = endpoint => {
  if (endpoint.method === "DELETE") return {}
  const path = endpoint.path.toLowerCase()
  const result = getSampleResult(endpoint)

  if (path.includes("user_permissions") || path.includes("auth/login") || path.includes("notifications")) {
    return result
  }
  if (
    endpoint.method === "GET" &&
    !endpoint.path.includes("{id}") &&
    !path.includes("get-primary-evaluation") &&
    !path.includes("get-secondary-evaluation")
  ) {
    return {
      count: 1,
      next: null,
      previous: null,
      results: [result],
    }
  }
  if (endpoint.method === "POST" && path.includes("send")) {
    return { message: "Request processed successfully", data: result }
  }
  return result
}

const getEndpointExamples = endpoint => {
  const url = `${apiBase}${normalizePath(endpoint.path)}`
  const body = getRequestBody(endpoint)
  const bodyBlock = body ? ` \\\n  -H "Content-Type: application/json" \\\n  -d '${stringifyJson(body)}'` : ""
  const curl = `curl -X ${endpoint.method} "${url}" \\\n  -H "Authorization: Bearer <access_token>"${bodyBlock}`
  const pythonBody = body ? `\npayload = ${stringifyJson(body)}\nresp = requests.${endpoint.method.toLowerCase()}(url, headers=headers, json=payload)` : `\nresp = requests.${endpoint.method.toLowerCase()}(url, headers=headers)`
  const python = `import requests\n\nurl = "${url}"\nheaders = {"Authorization": "Bearer <access_token>"}${pythonBody}\nprint(resp.json())`

  return {
    curl,
    python,
    response: stringifyJson(getSampleResponse(endpoint)),
    status: getSuccessStatus(endpoint.method),
  }
}

const EndpointExample = ({ endpoint, activeTab, copiedKey, exampleKey, onCopy, onTabChange }) => {
  const examples = getEndpointExamples(endpoint)
  const requestCode = activeTab === "python" ? examples.python : examples.curl
  const requestLabel = activeTab === "python" ? "Python" : "cURL"
  const requestCopyKey = `${exampleKey}-request`
  const responseCopyKey = `${exampleKey}-response`

  return (
    <div className="api-docs-example">
      <div className="api-docs-example-head">
        <div>
          {methodBadge(endpoint.method)}
          <code>{endpoint.path}</code>
        </div>
        <span>{endpoint.description}</span>
      </div>

      <div className="api-docs-example-label">REQUEST</div>
      <div className="api-docs-tabs">
        <button
          type="button"
          className={activeTab === "curl" ? "active" : ""}
          onClick={() => onTabChange("curl")}
        >
          cURL
        </button>
        <button
          type="button"
          className={activeTab === "python" ? "active" : ""}
          onClick={() => onTabChange("python")}
        >
          Python
        </button>
      </div>
      <div className="api-docs-dark-card">
        <div className="api-docs-dark-card-header">
          <span>{requestLabel}</span>
          <button type="button" onClick={() => onCopy(requestCopyKey, requestCode)}>
            <FaCopy />
            {copiedKey === requestCopyKey ? "Copied" : "Copy"}
          </button>
        </div>
        <pre>{requestCode}</pre>
      </div>

      <div className="api-docs-response-title">
        <span>RESPONSE</span>
        <span className="api-docs-status-dot">{examples.status}</span>
      </div>
      <div className="api-docs-dark-card">
        <div className="api-docs-dark-card-header">
          <span>JSON</span>
          <button type="button" onClick={() => onCopy(responseCopyKey, examples.response)}>
            <FaCopy />
            {copiedKey === responseCopyKey ? "Copied" : "Copy"}
          </button>
        </div>
        <pre>{examples.response}</pre>
      </div>
    </div>
  )
}

const ApiDocumentation = () => {
  const [query, setQuery] = useState("")
  const [activeModule, setActiveModule] = useState("All")
  const [requestTabs, setRequestTabs] = useState({})
  const [copiedKey, setCopiedKey] = useState("")

  const endpoints = useMemo(
    () =>
      modules.flatMap(module =>
        module.endpoints.map(endpoint => ({
          module: module.name,
          method: endpoint[0],
          path: endpoint[1],
          description: endpoint[2],
        }))
      ),
    []
  )

  const filteredEndpoints = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    return endpoints.filter(endpoint => {
      const matchesModule = activeModule === "All" || endpoint.module === activeModule
      const haystack = `${endpoint.module} ${endpoint.method} ${endpoint.path} ${endpoint.description}`.toLowerCase()
      return matchesModule && (!normalizedQuery || haystack.includes(normalizedQuery))
    })
  }, [activeModule, endpoints, query])

  const routeGroups = useMemo(() => {
    return pageRoutes.reduce((acc, route) => {
      const [module, path, purpose] = route
      acc[module] = acc[module] || []
      acc[module].push({ path, purpose })
      return acc
    }, {})
  }, [])

  const curlExample = `curl -X GET "${apiBase}/api/v1/candidates/?page=1&size=10" \\
  -H "Authorization: Bearer <access_token>" \\
  -H "Content-Type: application/json"`

  const handleCopy = (key, value) => {
    copyText(value)
    setCopiedKey(key)
    window.setTimeout(() => {
      setCopiedKey(currentKey => (currentKey === key ? "" : currentKey))
    }, 1600)
  }

  return (
    <div className="api-docs-page">
      <style>{`
        .api-docs-page {
          min-height: 100vh;
          padding: 84px 24px 24px;
          background: #f8fafc;
          color: #0f172a;
        }
        .api-docs-shell {
          display: grid;
          grid-template-columns: 280px minmax(0, 1fr);
          gap: 20px;
          align-items: start;
        }
        .api-docs-sidebar {
          position: sticky;
          top: 86px;
          max-height: calc(100vh - 110px);
          overflow: auto;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #ffffff;
          padding: 14px;
        }
        .api-docs-brand {
          display: flex;
          gap: 12px;
          align-items: center;
          padding-bottom: 14px;
          margin-bottom: 12px;
          border-bottom: 1px solid #e2e8f0;
        }
        .api-docs-brand-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 8px;
          background: #2563eb;
          color: #ffffff;
        }
        .api-docs-nav-button {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          border: 0;
          border-radius: 6px;
          padding: 9px 10px;
          background: transparent;
          color: #475569;
          font-size: 13px;
          text-align: left;
        }
        .api-docs-nav-button.active,
        .api-docs-nav-button:hover {
          background: #eff6ff;
          color: #1d4ed8;
        }
        .api-docs-main {
          min-width: 0;
        }
        .api-docs-hero {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 320px;
          gap: 18px;
          align-items: stretch;
          padding: 22px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #ffffff;
        }
        .api-docs-title {
          margin: 0 0 8px;
          font-size: 26px;
          line-height: 1.2;
        }
        .api-docs-subtitle {
          margin: 0;
          color: #64748b;
          font-size: 14px;
          max-width: 760px;
        }
        .api-docs-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }
        .api-docs-stat {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px;
          background: #f8fafc;
        }
        .api-docs-stat strong {
          display: block;
          font-size: 22px;
          line-height: 1;
        }
        .api-docs-stat span {
          color: #64748b;
          font-size: 12px;
        }
        .api-docs-toolbar {
          display: flex;
          gap: 10px;
          align-items: center;
          margin: 18px 0;
        }
        .api-docs-search {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
          border: 1px solid #dbe3ec;
          border-radius: 8px;
          padding: 0 12px;
          background: #ffffff;
        }
        .api-docs-search input {
          width: 100%;
          height: 42px;
          border: 0;
          outline: 0;
          color: #0f172a;
          background: transparent;
        }
        .api-docs-section {
          margin-top: 18px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #ffffff;
          overflow: hidden;
        }
        .api-docs-section-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 18px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .api-docs-section-header h2 {
          margin: 0;
          font-size: 18px;
        }
        .api-docs-section-body {
          padding: 18px;
        }
        .api-docs-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }
        .api-docs-panel {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 14px;
          background: #ffffff;
        }
        .api-docs-panel h3 {
          margin: 0 0 10px;
          font-size: 14px;
        }
        .api-docs-panel ul {
          margin: 0;
          padding-left: 0;
          color: #475569;
          font-size: 13px;
          list-style: none;
        }
        .api-docs-panel li {
          position: relative;
          padding-left: 16px;
          margin-bottom: 4px;
        }
        .api-docs-panel li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.72em;
          width: 4px;
          height: 4px;
          border-radius: 999px;
          background: #475569;
        }
        .api-docs-endpoint-table,
        .api-docs-route-table,
        .api-docs-response-table {
          width: 100%;
          border-collapse: collapse;
        }
        .api-docs-endpoint-table th,
        .api-docs-endpoint-table td,
        .api-docs-route-table th,
        .api-docs-route-table td,
        .api-docs-response-table th,
        .api-docs-response-table td {
          border-bottom: 1px solid #e2e8f0;
          padding: 11px 10px;
          vertical-align: top;
          font-size: 13px;
        }
        .api-docs-endpoint-table th,
        .api-docs-route-table th,
        .api-docs-response-table th {
          color: #475569;
          background: #f8fafc;
          font-weight: 700;
          text-align: left;
        }
        .api-docs-method {
          display: inline-flex;
          min-width: 56px;
          justify-content: center;
          border-radius: 999px;
          padding: 4px 9px;
          font-size: 11px;
          font-weight: 800;
        }
        .api-docs-code {
          display: block;
          overflow: auto;
          border-radius: 8px;
          padding: 14px;
          background: #0f172a;
          color: #e2e8f0;
          font-size: 12px;
          line-height: 1.6;
          white-space: pre;
        }
        .api-docs-example {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
          background: #ffffff;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
        }
        .api-docs-example-head {
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: flex-start;
          padding-bottom: 12px;
          margin-bottom: 14px;
          border-bottom: 1px solid #e2e8f0;
        }
        .api-docs-example-head > div {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          min-width: 280px;
        }
        .api-docs-example-head span {
          color: #475569;
          font-size: 13px;
          text-align: right;
        }
        .api-docs-example-label {
          margin: 10px 0 8px;
          color: #475569;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 1.2px;
        }
        .api-docs-tabs {
          display: flex;
          gap: 8px;
          border-bottom: 1px solid #dbe3ec;
          margin-bottom: 12px;
        }
        .api-docs-tabs button {
          border: 0;
          border-bottom: 2px solid transparent;
          background: transparent;
          color: #475569;
          padding: 8px 12px 9px;
          font-size: 13px;
          font-weight: 700;
        }
        .api-docs-tabs button.active {
          color: #2563eb;
          border-bottom-color: #2563eb;
        }
        .api-docs-dark-card {
          overflow: hidden;
          border-radius: 8px;
          background: #0b1222;
          color: #e2e8f0;
          border: 1px solid #1e293b;
          box-shadow: 0 10px 18px rgba(15, 23, 42, 0.18);
        }
        .api-docs-dark-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px 7px;
          border-bottom: 1px solid #1e293b;
        }
        .api-docs-dark-card-header span {
          color: #38bdf8;
          border-bottom: 2px solid #38bdf8;
          padding-bottom: 5px;
          font-size: 12px;
          font-weight: 800;
        }
        .api-docs-dark-card-header button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 0;
          background: transparent;
          color: #cbd5e1;
          font-size: 12px;
          font-weight: 700;
        }
        .api-docs-dark-card pre {
          margin: 0;
          padding: 18px 14px 20px;
          overflow: auto;
          color: #f8fafc;
          font-size: 12px;
          line-height: 1.65;
          white-space: pre;
        }
        .api-docs-response-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 18px 0 10px;
          color: #475569;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 1.2px;
        }
        .api-docs-status-dot {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          border-radius: 999px;
          padding: 4px 9px;
          background: #dcfce7;
          color: #16a34a;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0;
        }
        .api-docs-status-dot::before {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: currentColor;
        }
        .api-docs-copy {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          background: #ffffff;
          color: #334155;
          padding: 8px 10px;
          font-size: 12px;
          font-weight: 700;
        }
        .api-docs-chip {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 4px 9px;
          background: #f1f5f9;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
        }
        .api-docs-route-group {
          margin-top: 14px;
        }
        .api-docs-route-group h3 {
          margin: 0 0 8px;
          font-size: 15px;
        }
        @media (max-width: 1100px) {
          .api-docs-shell,
          .api-docs-hero {
            grid-template-columns: 1fr;
          }
          .api-docs-sidebar {
            position: static;
            max-height: none;
          }
          .api-docs-grid,
          .api-docs-stats {
            grid-template-columns: 1fr;
          }
          .api-docs-example-head {
            display: block;
          }
          .api-docs-example-head span {
            display: block;
            text-align: left;
            margin-top: 8px;
          }
        }
      `}</style>

      <div className="api-docs-shell">
        <aside className="api-docs-sidebar">
          <div className="api-docs-brand">
            <span className="api-docs-brand-icon">
              <FaBookOpen />
            </span>
            <div>
              <strong>ATS API</strong>
              <div style={{ color: "#64748b", fontSize: 12 }}>API documentation v1</div>
            </div>
          </div>
          <button
            type="button"
            className={`api-docs-nav-button ${activeModule === "All" ? "active" : ""}`}
            onClick={() => setActiveModule("All")}
          >
            <span>All endpoints</span>
            <span className="api-docs-chip">{endpoints.length}</span>
          </button>
          {modules.map(module => (
            <button
              type="button"
              key={module.name}
              className={`api-docs-nav-button ${activeModule === module.name ? "active" : ""}`}
              onClick={() => setActiveModule(module.name)}
            >
              <span>{module.name}</span>
              <span className="api-docs-chip">{module.endpoints.length}</span>
            </button>
          ))}
        </aside>

        <main className="api-docs-main">
          <section className="api-docs-hero">
            <div>
              <h1 className="api-docs-title">ATS API Documentation</h1>
              <p className="api-docs-subtitle">
                A single reference for ATS pages, tasks, API request methods, payload expectations,
                response shapes and common errors. Endpoints are grouped from the React pages and
                Django URL modules used by the current application.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
                <span className="api-docs-chip">Base URL: {apiBase}</span>
                <span className="api-docs-chip">Auth: Bearer token after login</span>
                <span className="api-docs-chip">Content: JSON or FormData</span>
              </div>
            </div>
            <div className="api-docs-stats">
              <div className="api-docs-stat">
                <strong>{pageRoutes.length}</strong>
                <span>ATS pages covered</span>
              </div>
              <div className="api-docs-stat">
                <strong>{modules.length}</strong>
                <span>Functional modules</span>
              </div>
              <div className="api-docs-stat">
                <strong>{endpoints.length}</strong>
                <span>Documented endpoints</span>
              </div>
            </div>
          </section>

          <div className="api-docs-toolbar">
            <label className="api-docs-search">
              <FaSearch color="#64748b" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search pages, tasks, methods or endpoints..."
              />
            </label>
            <button className="api-docs-copy" type="button" onClick={() => handleCopy("toolbar-curl", curlExample)}>
              <FaCopy />
              {copiedKey === "toolbar-curl" ? "Copied" : "Copy cURL"}
            </button>
          </div>

          <section className="api-docs-section">
            <div className="api-docs-section-header">
              <FaCode />
              <h2>Request Pattern</h2>
            </div>
            <div className="api-docs-section-body">
              <div className="api-docs-grid">
                <div className="api-docs-panel">
                  <h3>Authentication</h3>
                  <ul>
                    <li>Login returns access data saved in local storage.</li>
                    <li>Authenticated requests send Authorization: Bearer access_token.</li>
                    <li>Public flows include autologin, consent token and unsubscribe links.</li>
                  </ul>
                </div>
                <div className="api-docs-panel">
                  <h3>Query Parameters</h3>
                  <ul>
                    <li>Lists commonly use page, size, page_size, search and company_id.</li>
                    <li>Reports add recruiter, job, candidate and date filters.</li>
                    <li>Lookups use ids such as country_id, state_id and category_id.</li>
                  </ul>
                </div>
                <div className="api-docs-panel">
                  <h3>Body Types</h3>
                  <ul>
                    <li>Most CRUD endpoints use JSON request bodies.</li>
                    <li>Resume, JD, CSV and document uploads use multipart FormData.</li>
                    <li>PATCH endpoints send only changed status, stage or config fields.</li>
                  </ul>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
                <strong>Example cURL</strong>
                <button className="api-docs-copy" type="button" onClick={() => handleCopy("pattern-curl", curlExample)}>
                  <FaCopy />
                  {copiedKey === "pattern-curl" ? "Copied" : "Copy"}
                </button>
              </div>
              <code className="api-docs-code">{curlExample}</code>
            </div>
          </section>

          <section className="api-docs-section">
            <div className="api-docs-section-header">
              <FaLayerGroup />
              <h2>Module Tasks and Endpoints</h2>
            </div>
            <div className="api-docs-section-body">
              {modules
                .filter(module => activeModule === "All" || module.name === activeModule)
                .map(module => (
                  <div key={module.name} style={{ marginBottom: 24 }}>
                    <h3 style={{ margin: "0 0 10px", fontSize: 18 }}>{module.name}</h3>
                    <div className="api-docs-grid" style={{ marginBottom: 14 }}>
                      <div className="api-docs-panel">
                        <h3>ATS pages</h3>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {module.pages.map(page => (
                            <Link className="api-docs-chip" to={page.replace("/:id", "")} key={page}>
                              {page}
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div className="api-docs-panel">
                        <h3>Tasks</h3>
                        <ul>
                          {module.tasks.map(task => (
                            <li key={task}>{task}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="api-docs-panel">
                        <h3>Request and Response</h3>
                        <p style={{ margin: "0 0 8px", color: "#475569", fontSize: 13 }}>{module.request}</p>
                        <p style={{ margin: 0, color: "#475569", fontSize: 13 }}>{module.response}</p>
                      </div>
                    </div>
                    <div>
                      {module.endpoints.map(endpoint => {
                        const endpointInfo = {
                          method: endpoint[0],
                          path: endpoint[1],
                          description: endpoint[2],
                        }
                        const exampleKey = `${module.name}-${endpointInfo.method}-${endpointInfo.path}-${endpointInfo.description}`
                        const activeTab = requestTabs[exampleKey] || "curl"

                        return (
                          <EndpointExample
                            key={exampleKey}
                            endpoint={endpointInfo}
                            activeTab={activeTab}
                            copiedKey={copiedKey}
                            exampleKey={exampleKey}
                            onCopy={handleCopy}
                            onTabChange={tab =>
                              setRequestTabs(prev =>
                                Object.assign({}, prev, { [exampleKey]: tab })
                              )
                            }
                          />
                        )
                      })}
                    </div>
                  </div>
                ))}
            </div>
          </section>

          <section className="api-docs-section">
            <div className="api-docs-section-header">
              <FaRoute />
              <h2>Critical Workflow Reference</h2>
              <span className="api-docs-chip">Backend contract</span>
            </div>
            <div className="api-docs-section-body">
              <div className="api-docs-grid">
                <div className="api-docs-panel">
                  <h3>Company automation</h3>
                  <p><code>GET/PATCH /api/v1/company/{"{company_id}"}/</code></p>
                  <ul>
                    <li>Always identify the company with <code>company_id</code>; never use name or domain as the identifier.</li>
                    <li>Automation fields include resume scoring, automatic screening invites, calling agent, threshold, and TalentScan delivery mode.</li>
                    <li>PATCH should contain only changed fields. Authorized SuperAdmin/company administrators are enforced by the backend.</li>
                  </ul>
                </div>
                <div className="api-docs-panel">
                  <h3>Calling notifications</h3>
                  <ul>
                    <li><strong>VGUS:</strong> Email and SMS available; WhatsApp disabled.</li>
                    <li><strong>Non-VGUS:</strong> Email and WhatsApp available; SMS disabled.</li>
                    <li>Disabled channels must not send. Bulk notification failures return a warning detail/reason and preserve the recruiter selections for retry.</li>
                  </ul>
                </div>
                <div className="api-docs-panel">
                  <h3>Verification and dependencies</h3>
                  <ul>
                    <li>Last verified against the current frontend API clients and route catalog.</li>
                    <li>Bearer authentication is required unless an endpoint is explicitly marked public.</li>
                    <li>Async workflows may depend on Celery, Redis, Vitel, CalendNow, email, SMS, WhatsApp, or TalentScan providers.</li>
                  </ul>
                </div>
              </div>

              <div className="api-docs-grid" style={{ marginTop: 14 }}>
                <div className="api-docs-panel">
                  <h3>Interview booking</h3>
                  <p><code>GET /api/v1/interview-booking/{"{token}"}/availability/</code></p>
                  <p><code>POST /api/v1/interview-booking/{"{token}"}/</code></p>
                  <pre className="api-docs-code">{`{
  "selected_date": "2026-09-23",
  "selected_time": "20:30",
  "timezone": "Asia/Kolkata",
  "booking_type": "screening_call"
}`}</pre>
                  <p>Use <code>screening_call</code> for the Vitel workflow and <code>talent_scan_test</code> for TalentScan. Screening and TalentScan bookings remain separate.</p>
                </div>
                <div className="api-docs-panel">
                  <h3>TalentScan delivery</h3>
                  <pre className="api-docs-code">{`{
  "selected_date": "2026-09-23",
  "selected_time": "20:30",
  "timezone": "Asia/Kolkata",
  "booking_type": "talent_scan_test"
}`}</pre>
                  <ul>
                    <li><code>direct</code> assigns/sends TalentScan immediately after the qualifying workflow.</li>
                    <li><code>calendnow</code> sends or requires a CalendNow booking.</li>
                    <li>Dates may be stored in UTC; display timezone follows company domain. VGUS uses US Eastern; other companies use Asia/Kolkata.</li>
                  </ul>
                </div>
                <div className="api-docs-panel">
                  <h3>Response states</h3>
                  <ul>
                    <li><code>booked</code>: show the confirmation and scheduled details.</li>
                    <li><code>uncertain</code>: show a pending confirmation; do not treat it as success.</li>
                    <li>Provider failures may be retried by the backend task workflow. Inspect API response detail and Celery/Redis worker logs when a provider is unavailable.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="api-docs-section">
            <div className="api-docs-section-header">
              <FaSearch />
              <h2>Endpoint Index</h2>
            </div>
            <div className="api-docs-section-body">
              <div style={{ overflowX: "auto" }}>
                <table className="api-docs-endpoint-table">
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Endpoint</th>
                      <th>Module</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEndpoints.map(endpoint => (
                      <tr key={`${endpoint.method}-${endpoint.path}-${endpoint.description}`}>
                        <td>{methodBadge(endpoint.method)}</td>
                        <td>
                          <code>{endpoint.path}</code>
                        </td>
                        <td>{endpoint.module}</td>
                        <td>{endpoint.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredEndpoints.length === 0 && (
                <div style={{ color: "#64748b", padding: 16 }}>No endpoints match the current search.</div>
              )}
            </div>
          </section>

          <section className="api-docs-section">
            <div className="api-docs-section-header">
              <FaRoute />
              <h2>ATS Page Coverage</h2>
            </div>
            <div className="api-docs-section-body">
              {Object.entries(routeGroups).map(([group, routes]) => (
                <div className="api-docs-route-group" key={group}>
                  <h3>{group}</h3>
                  <div style={{ overflowX: "auto" }}>
                    <table className="api-docs-route-table">
                      <thead>
                        <tr>
                          <th style={{ width: 280 }}>Route</th>
                          <th>Purpose</th>
                        </tr>
                      </thead>
                      <tbody>
                        {routes.map(route => (
                          <tr key={route.path}>
                            <td>
                              <code>{route.path}</code>
                            </td>
                            <td>{route.purpose}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="api-docs-section">
            <div className="api-docs-section-header">
              <FaBookOpen />
              <h2>Response and Error Matrix</h2>
            </div>
            <div className="api-docs-section-body">
              <table className="api-docs-response-table">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Status</th>
                    <th style={{ width: 180 }}>Meaning</th>
                    <th>Body / Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {commonResponses.map(response => (
                    <tr key={response.status}>
                      <td>
                        <code>{response.status}</code>
                      </td>
                      <td>{response.meaning}</td>
                      <td>{response.body}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default ApiDocumentation
